/**
 * Key exchange service — shares EC P-384 public keys via the @keys relay user.
 *
 * Each user has two public keys:
 *   - encrypt_key: ECDH P-384 public key (for ECDH key agreement)
 *   - sign_key:    ECDSA P-384 public key (for signature verification)
 *
 * Both are stored as JWK strings in the backend's public_keys table and in
 * local IndexedDB via keyStorageService.
 */

import {
  generateKeyPair,
  base64ToArrayBuffer,
  arrayBufferToBase64,
  importEncryptPublicKey,
  importSignPublicKey,
} from "@/utils/crypto";
import {
  storeKeyPair,
  storePublicKey,
  getActiveKeySet,
  getActiveKeyId,
  hasPrivateKey,
  getPublicKeySetByUsername,
  markGroupKeysStale,
} from "./keyStorageService";
import { sendUserMessage } from "./conversationService";
import { KEYS_USERNAME } from "@/constants/message";

export { KEYS_USERNAME };

const EXTERNAL_ADDRESS_TYPE = 4;

export const E2EE_CMD_GET_ALL = "getallkeys";
export const E2EE_CMD_GET = "getkeys";
export const E2EE_CMD_SEND = "sendkeys";
export const E2EE_CMD_DELETE = "deletekeys";

export interface KeyExchangeMessage {
  cls?: "keys";
  cmd?: string;
  user_id?: number;
  username?: string;
  encrypt_key?: string;
  sign_key?: string;
  timestamp_generated?: number;
  public_keys?: KeyExchangeMessage[];
  error?: string;
}

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

// Dedup concurrent initializeEncryption calls for the same user.
// On first login, multiple hook instances + the websocket middleware all call
// this simultaneously. Without the guard each generates a different key pair,
// races to write it, and ends up with a stale keyId. With it, they all await
// the same promise and receive the same keyId.
const initInFlight = new Map<number, Promise<string>>();

// Resolvers waiting for a specific username's key to arrive via WebSocket.
// requestPublicKey registers here before sending the HTTP request so the
// immediate retry in encryptionProcessor can wait for the actual arrival.
const pendingKeyArrivals = new Map<string, Array<() => void>>();

/**
 * Notifies any waiting requestPublicKey callers that a key for `username`
 * has been stored in IndexedDB. Called from handleReceivedPublicKey.
 */
export function notifyKeyArrived(username: string): void {
  const resolvers = pendingKeyArrivals.get(username);
  if (resolvers?.length) {
    pendingKeyArrivals.delete(username);
    resolvers.forEach((r) => r());
  }
}

// Resolved key cache: survives logout/re-login within the same JS runtime.
// Avoids the async IDB round-trip on every re-login so useEncryption can
// skip the isLoading→isInitialized transition entirely.
const resolvedKeyIds = new Map<number, string>();

/** Returns the cached keyId for userId if already resolved this session, else null. */
export function getResolvedKeyId(userId: number): string | null {
  return resolvedKeyIds.get(userId) ?? null;
}

/**
 * Generates an EC P-384 key pair set if one doesn't exist, stores it locally,
 * and returns the key ID. Concurrent calls for the same userId are coalesced.
 * Subsequent calls return immediately from the in-memory cache.
 */
export async function initializeEncryption(userId: number): Promise<string> {
  // Fast path: already resolved in this JS runtime (re-login without page reload)
  const cached = resolvedKeyIds.get(userId);
  if (cached) return cached;

  const existing = initInFlight.get(userId);
  if (existing) return existing;

  const promise = (async () => {
    const hasKey = await hasPrivateKey(userId);

    if (hasKey) {
      const keyId = await getActiveKeyId(userId);
      if (keyId) {
        console.log("Encryption already initialized with key:", keyId);
        resolvedKeyIds.set(userId, keyId);
        return keyId;
      }
    }

    console.log("Generating new EC P-384 key pairs...");
    const keyPairInfo = await generateKeyPair();
    await storeKeyPair(userId, keyPairInfo, true);
    console.log("Key pairs generated and stored. Key ID:", keyPairInfo.keyId);
    resolvedKeyIds.set(userId, keyPairInfo.keyId);

    return keyPairInfo.keyId;
  })();

  initInFlight.set(userId, promise);
  promise.finally(() => initInFlight.delete(userId));
  promise.then(requestPersistentStorage, () => {});
  return promise;
}

let persistRequested = false;

/**
 * Asks the browser not to evict our storage (Safari drops it after ~7 days unvisited), which would
 * lose the key pair, owned group keys and readable history. Once per page; Firefox prompts once per site.
 */
function requestPersistentStorage(): void {
  if (persistRequested || !navigator.storage?.persist) return;
  persistRequested = true;
  navigator.storage.persisted()
    .then((persisted) => persisted || navigator.storage.persist())
    .catch(() => {});
}

// ---------------------------------------------------------------------------
// Key sharing
// ---------------------------------------------------------------------------

/**
 * Builds the `sendkeys <payload>` command string to upload both public keys
 * to the @keys relay.
 *
 * Keys are sent as X509/SPKI Base64 so both Android (BouncyCastle X509EncodedKeySpec)
 * and web (crypto.subtle.importKey("spki", ...)) can parse them.
 * The receive path (`parsePublicKeyString`) handles both SPKI and legacy JWK formats
 * for backward compatibility.
 */
export async function createPublicKeyShareMessage(userId: number): Promise<string> {
  const keySet = await getActiveKeySet(userId);
  if (!keySet) throw new Error("No active key set. Initialize encryption first.");

  const [encryptCryptoKey, signCryptoKey] = await Promise.all([
    importEncryptPublicKey(keySet.encryptPublicKeyJwk),
    importSignPublicKey(keySet.signPublicKeyJwk),
  ]);
  const [encryptSpki, signSpki] = await Promise.all([
    crypto.subtle.exportKey("spki", encryptCryptoKey),
    crypto.subtle.exportKey("spki", signCryptoKey),
  ]);

  const payload = {
    encrypt_key: arrayBufferToBase64(encryptSpki),
    sign_key: arrayBufferToBase64(signSpki),
    // Use the key's own creation time, not the current time, so the timestamp
    // stays stable across browser sessions (matches Android's keyGenTimeStamp behaviour).
    timestamp_generated: Math.floor(keySet.createdAt / 1000),
  };

  return `${E2EE_CMD_SEND} ${JSON.stringify(payload)}`;
}

// ---------------------------------------------------------------------------
// Key requests
// ---------------------------------------------------------------------------

export async function createPublicKeyRequestMessage(targetUsername: string): Promise<string> {
  return `${E2EE_CMD_GET} ${targetUsername}`;
}

export function parseKeyExchangeMessage(message: string): KeyExchangeMessage | null {
  try {
    const parsed = JSON.parse(message);
    if (
      parsed.cls === "keys" &&
      (parsed.cmd === E2EE_CMD_GET || parsed.cmd === E2EE_CMD_GET_ALL)
    ) {
      return parsed as KeyExchangeMessage;
    }
    return null;
  } catch {
    return null;
  }
}

export async function sendKeySystemCommand(command: string, userId: number): Promise<any> {
  return await sendUserMessage({
    cmd: "SendUserMessage",
    message: command,
    address: KEYS_USERNAME,
    addressId: 0,
    address_type: EXTERNAL_ADDRESS_TYPE,
    lat: 0,
    lng: 0,
    media: null,
    media_filename: null,
    media_type: 0,
    sendtime: Date.now(),
    userid: userId,
  });
}

// How long to wait for the @keys WebSocket reply before giving up.
const KEY_ARRIVAL_TIMEOUT_MS = 5000;

/**
 * Requests a user's public key from @keys and waits for it to arrive.
 *
 * The backend processes `getkeys <username>` synchronously during the HTTP
 * request but delivers the result as a received WebSocket message (via
 * saveRMessage → WebSocket → processAndDispatch → handleReceivedPublicKey).
 * Registering a resolver in pendingKeyArrivals BEFORE sending ensures the
 * arrival notification is never missed even if the WS reply races ahead.
 */
export async function requestPublicKey(
  targetUsername: string,
  currentUserId: number
): Promise<void> {
  // Register the arrival listener before sending so we never miss a fast reply.
  const keyArrival = new Promise<void>((resolve) => {
    const timeoutId = setTimeout(() => {
      const resolvers = pendingKeyArrivals.get(targetUsername) ?? [];
      const idx = resolvers.indexOf(wrappedResolve);
      if (idx !== -1) resolvers.splice(idx, 1);
      resolve();
    }, KEY_ARRIVAL_TIMEOUT_MS);

    const wrappedResolve = () => {
      clearTimeout(timeoutId);
      resolve();
    };

    const existing = pendingKeyArrivals.get(targetUsername) ?? [];
    existing.push(wrappedResolve);
    pendingKeyArrivals.set(targetUsername, existing);
  });

  const command = await createPublicKeyRequestMessage(targetUsername);
  await sendKeySystemCommand(command, currentUserId);
  await keyArrival;
}

// In-flight refreshes by username (concurrent callers share one @keys fetch), and the usernames
// refreshed this page session — including a timed-out attempt, so a user with no published keys
// can't loop (each @keys error reply bumps keyExchangeVersion, which would re-trigger a fetch).
const refreshesInFlight = new Map<string, Promise<void>>();
const refreshedThisSession = new Set<string>();

/**
 * Re-fetches a user's public key from @keys. Callers pick the moment — first conversation open
 * per session, a decrypt failure newer than our copy (evidence), or the manual sync — never per
 * message.
 * Non-fatal: on failure the cached copy stays.
 */
export function refreshPublicKey(targetUsername: string, currentUserId: number): Promise<void> {
  const inFlight = refreshesInFlight.get(targetUsername);
  if (inFlight) return inFlight;
  const refresh = requestPublicKey(targetUsername, currentUserId)
    .then(() => { refreshedThisSession.add(targetUsername); })
    .catch((err) => console.warn("[E2EE] Key refresh failed for", targetUsername, err))
    .finally(() => refreshesInFlight.delete(targetUsername));
  refreshesInFlight.set(targetUsername, refresh);
  return refresh;
}

/** True once a refresh of `username`'s key has been attempted (and not errored) this page session. */
export const wasRefreshedThisSession = (username: string) => refreshedThisSession.has(username);

/** On logout: the next login (same tab, no reload) re-fetches each peer's key on first open. */
export function resetKeyRefreshState(): void {
  refreshedThisSession.clear();
}

// ---------------------------------------------------------------------------
// Receiving a peer's public key
// ---------------------------------------------------------------------------

/**
 * Parses a public key string that may be either:
 *   - JWK JSON  (web clients): {"kty":"EC","crv":"P-384","x":"...","y":"..."}
 *   - X509/SPKI Base64 (Android clients): DER-encoded public key, base64-encoded
 *
 * Returns the key as a JWK so the rest of the codebase stays uniform.
 */
export async function parsePublicKeyString(
  keyStr: string,
  algorithm: "ECDH" | "ECDSA"
): Promise<JsonWebKey | null> {
  // Try JWK first (web clients)
  try {
    const parsed = JSON.parse(keyStr);
    if (parsed && typeof parsed === "object" && parsed.kty === "EC") {
      return parsed as JsonWebKey;
    }
  } catch { /* not JSON — try X509/SPKI */ }

  // Try X509/SPKI Base64 (Android clients)
  try {
    const spkiBytes = base64ToArrayBuffer(keyStr);
    const params =
      algorithm === "ECDH"
        ? { name: "ECDH", namedCurve: "P-384" }
        : { name: "ECDSA", namedCurve: "P-384" };
    const usages: KeyUsage[] = algorithm === "ECDH" ? [] : ["verify"];
    const cryptoKey = await crypto.subtle.importKey("spki", spkiBytes, params, true, usages);
    return crypto.subtle.exportKey("jwk", cryptoKey);
  } catch (err) {
    console.warn("[E2EE] Failed to parse key as X509/SPKI:", err);
    return null;
  }
}

/**
 * Stores both public keys received from another user via the @keys relay.
 * Accepts both JWK (web) and X509/SPKI Base64 (Android) key formats.
 * `sign_key` defaults to `encrypt_key` if absent (backward compat with older
 * clients that only sent one key).
 *
 * @returns null if skipped; else whether it replaced a different key pair (user rotated keys).
 */
export async function handleReceivedPublicKey(message: KeyExchangeMessage): Promise<{ keyChanged: boolean } | null> {
  if (!message.encrypt_key || !message.user_id || !message.username) {
    throw new Error("Invalid public key message from backend");
  }

  const encryptPublicKeyJwk = await parsePublicKeyString(message.encrypt_key, "ECDH");

  if (!encryptPublicKeyJwk) {
    console.warn(`[E2EE] Could not parse encrypt key for ${message.username} — skipping`);
    return null;
  }

  // Reject legacy RSA keys — only EC P-384 keys are compatible with the current
  // ECDH encryption scheme. Users with old RSA keys need to re-login to regenerate.
  if (encryptPublicKeyJwk.kty !== "EC" || encryptPublicKeyJwk.crv !== "P-384") {
    console.warn(`[E2EE] Ignoring non-EC key for ${message.username} (kty=${encryptPublicKeyJwk.kty})`);
    return null;
  }

  const signPublicKeyJwk = message.sign_key
    ? (await parsePublicKeyString(message.sign_key, "ECDSA")) ?? encryptPublicKeyJwk
    : encryptPublicKeyJwk;

  const previousTimestamp = (await getPublicKeySetByUsername(message.username))?.timestampGenerated;

  await storePublicKey(
    "imported-" + message.user_id,
    message.user_id,
    message.username,
    encryptPublicKeyJwk,
    signPublicKeyJwk,
    message.timestamp_generated
  );

  // Number(): the router may relay timestamp_generated as a string.
  const keyChanged = !!previousTimestamp && !!message.timestamp_generated &&
    Number(previousTimestamp) !== Number(message.timestamp_generated);

  // An owner's key pair and its group keys are stored — and lost — together (Android: one
  // Keystore master key; web: IDB), so a new key pair supersedes every group key from this
  // owner. Flag them before notifying waiters, so a caller awaiting this key sees it.
  if (keyChanged) {
    const owner = message.username;
    console.warn(`[GroupKey] Owner "${owner}" has a new key pair — marking their group keys stale`);
    await markGroupKeysStale((key) => key.ownerUsername === owner);
  }

  console.log(`Stored public keys for user ${message.username} (ID: ${message.user_id})`);
  notifyKeyArrived(message.username);
  return { keyChanged };
}

export function isKeyExchangeMessage(message: string): boolean {
  return parseKeyExchangeMessage(message) !== null;
}
